import { useState, useCallback, memo, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./GetStarted.scss";

import Button from "../../../components/Button/Button";
import GetStartedBg from "../../../assets/png/get-started-bg.png";
import GetStartedImg2 from "../../../assets/png/get-started-img-2.png";
import GetStartedImg3 from "../../../assets/png/get-started-img-3.png";

import useWindowSize from "../../../hooks/useWindowResize";
import ImagePreloader from "../../../components/ImagePreloader/ImagePreloader";

function GetStarted() {
  const { width } = useWindowSize();
  const isMobile = width <= 480;
  const isOverlap = width <= 992;

  const paymentTypeData = useMemo(
    () => ({
      Trade: {
        img: GetStartedImg3,
        title: (
          <>
            <span className="break-first-title">
              Effortless {isMobile ? <br /> : ""} crypto{" "}
              {isMobile ? <br /> : ""} conversion
            </span>
          </>
        ),
        desc: "Experience seamless trading on TorumPay. Easily exchange between cryptocurrencies and Ringgit with ease and security.",
      },
      Send: {
        img: GetStartedImg2,
        title: (
          <>
            <span className="break-first-title">
              Swift crypto transfers on torumpay
            </span>
          </>
        ),
        desc: "Experience swift and secure cryptocurrency transfers with TorumPay. Send digital assets to your friends instantly, hassle-free.",
      },
    }),
    [isMobile]
  );

  const paymentKeys = useMemo(
    () => Object.keys(paymentTypeData),
    [paymentTypeData]
  );
  const [paymentType, setPaymentType] = useState(paymentKeys[0] ?? "");
  const selectPaymentType = useCallback((type) => setPaymentType(type), []);

  const active =
    paymentTypeData[paymentType] ?? paymentTypeData[paymentKeys[0]];
  const imageSources = useMemo(
    () => paymentKeys.map((k) => paymentTypeData[k].img).filter(Boolean),
    [paymentKeys, paymentTypeData]
  );

  useEffect(() => {
    if (paymentKeys.length <= 1) return;
    const id = setInterval(() => {
      setPaymentType((prev) => {
        const idx = paymentKeys.indexOf(prev);
        const next = (idx + 1) % paymentKeys.length;
        return paymentKeys[next];
      });
    }, 5000);
    return () => clearInterval(id);
  }, [paymentKeys]);

  const MemoizedButton = memo(({ children, ...props }) => (
    <Button {...props}>{children}</Button>
  ));

  const contentVariants = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 },
  };

  const transitionSettings = {
    duration: 0.3,
    ease: "easeInOut",
  };

  return (
    <div className="tp-main-getstarted">
      <ImagePreloader srcList={imageSources} />

      {isOverlap ? (
        <div className="tp-main-getstarted-wrapper ">
          <div className="getstarted-left-content">
            <p className="getstarted-top-title">
              Getting started with TorumPay
            </p>

            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={paymentType}
                variants={contentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={transitionSettings}
                className="getstarted-left-content-wrapper "
              >
                <p className="getstarted-title">{active?.title ?? ""}</p>

                <div className="mobile">
                  {active?.img && (
                    <img
                      src={active.img}
                      alt="Selected Payment Type"
                      className="payment-img"
                    />
                  )}
                </div>

                <p className="getstarted-desc">{active?.desc ?? ""}</p>
              </motion.div>
            </AnimatePresence>

            <div className="button-wrapper">
              <MemoizedButton
                className="outlined-dark fixed-width"
                onClick={() => selectPaymentType("Trade")}
                active={paymentType === "Trade"}
              >
                Trade
              </MemoizedButton>

              <MemoizedButton
                className="outlined-dark fixed-width"
                onClick={() => selectPaymentType("Send")}
                active={paymentType === "Send"}
              >
                Send
              </MemoizedButton>

              <img
                className="get-started-bg"
                src={GetStartedBg}
                alt="get-started-bg"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="tp-main-getstarted-wrapper">
          <div className="getstarted-left-content">
            <p className="getstarted-top-title">
              Getting started with TorumPay
            </p>

            <AnimatePresence mode="wait">
              <motion.div
                key={paymentType}
                variants={contentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={transitionSettings}
                className="getstarted-left-content-wrapper"
              >
                <p className="getstarted-title">{active?.title ?? ""}</p>
                <p className="getstarted-desc">{active?.desc ?? ""}</p>
              </motion.div>
            </AnimatePresence>

            <Button className="filled-expand reversed-rr disabled">
              Coming Soon
            </Button>
          </div>

          <div className="getstarted-right-content">
            <img
              className="get-started-bg"
              src={GetStartedBg}
              alt="get-started-bg"
            />

            <AnimatePresence mode="wait">
              <motion.div
                key={paymentType}
                variants={contentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={transitionSettings}
              >
                {active?.img && (
                  <img
                    src={active.img}
                    alt="Selected Payment Type"
                    className="payment-img"
                  />
                )}
              </motion.div>
            </AnimatePresence>

            <div className="button-wrapper">
              <MemoizedButton
                className="outlined-dark fixed-width"
                onClick={() => selectPaymentType("Trade")}
                active={paymentType === "Trade"}
              >
                Trade
              </MemoizedButton>

              <MemoizedButton
                className="outlined-dark fixed-width"
                onClick={() => selectPaymentType("Send")}
                active={paymentType === "Send"}
              >
                Send
              </MemoizedButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GetStarted;
